import {
  useCallback, useEffect, useRef, useState,
} from 'react';

type UsePingPongTimeoutOptions = {
  enabled: boolean;
  timeoutMs: number;
  sendPing: () => void | Promise<void>;
  onTimeout: () => void;
  intervalMs?: number;
  continueAfterTimeout?: boolean;
};

type PingStatus = 'idle' | 'waiting' | 'ok' | 'timeout';

function useLatestRef<T>(value: T) {
  const ref = useRef(value);

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref;
}

export default function usePingPongTimeout({
  enabled,
  timeoutMs,
  sendPing,
  onTimeout,
  intervalMs,
  continueAfterTimeout = false,
}: UsePingPongTimeoutOptions) {
  const [status, setStatus] = useState<PingStatus>('idle');

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const waitingForPongRef = useRef(false);

  const sendPingRef = useLatestRef(sendPing);
  const onTimeoutRef = useLatestRef(onTimeout);

  const clearTimeoutTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const clearIntervalTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    clearTimeoutTimer();
    clearIntervalTimer();
    waitingForPongRef.current = false;
    setStatus('idle');
  }, [clearTimeoutTimer, clearIntervalTimer]);

  const pingNow = useCallback(() => {
    if (!enabled) return;

    clearTimeoutTimer();

    waitingForPongRef.current = true;
    setStatus('waiting');
    sendPingRef.current();

    timeoutRef.current = setTimeout(() => {
      if (!waitingForPongRef.current) return;

      waitingForPongRef.current = false;
      setStatus('timeout');

      onTimeoutRef.current();
      if (!continueAfterTimeout) clearIntervalTimer();
    }, timeoutMs);
  }, [enabled, timeoutMs, continueAfterTimeout, clearTimeoutTimer, clearIntervalTimer, sendPingRef, onTimeoutRef]);

  const onPongReceived = useCallback(() => {
    if (!enabled) return;
    if (!waitingForPongRef.current) return;

    waitingForPongRef.current = false;
    clearTimeoutTimer();
    setStatus('ok');
  }, [enabled, clearTimeoutTimer]);

  useEffect(() => {
    if (!enabled) { stop(); return undefined; }

    pingNow();
    if (intervalMs != null) {
      intervalRef.current = setInterval(() => {
        pingNow();
      }, intervalMs);
    }
    return stop;
  }, [enabled, intervalMs, pingNow, stop]);

  return {
    status,
    pingNow,
    onPongReceived,
    stop,
  };
}
